﻿import { Box, CircularProgress, Grid, Typography, Paper, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import MultiDatePicker from '../../../components/documented/MultiDatePicker/MultiDatePicker';
import { DateGroupType } from '../../../model/active/dateGroupType';
import { PassiveParameterTypeEnum } from '../../../model/passive/enum/passiveParameterTypeEnum';
import { IProcessedActivityTrackerInterval } from '../../../model/passive/activityTrackerData';
import { getPassiveParameterData } from '../../../redux/actions/dailyParameterActions';
import { useAppThunkDispatch } from '../../../redux/configureStore';
import { processedInsoleUsageData } from '../../../redux/selectors/dailyParametersSelectors';
import { selectedMetricDate } from '../../../redux/selectors/dailyMetricsSelectors';
import CombinedActivityChart from './charts/CombinedActivityChart';

interface Props {
  patientId?: string;
  selectedDateRange?: [Date | null, Date | null];
}

const Root = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
});

const DatePickerContainer = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  marginBottom: '16px',
  gap: '16px',
});

const ResetZoomButton = styled(Button)({
  marginRight: 'auto',
});

const ChartPaper = styled(Paper)({
  padding: '20px',
  marginBottom: '24px',
  position: 'relative',
});

const ChartContainer = styled(Box)({
  height: '350px',
  marginTop: '16px',
  position: 'relative',
});

const LoadingContainer = styled('div')({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.7)',
  zIndex: 10,
});

const StatsContainer = styled(Grid)({
  marginTop: '20px',
});

const StatsCard = styled(Box)({
  padding: '16px',
  backgroundColor: '#f5f5f5',
  borderRadius: '8px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
});

const StatValue = styled(Typography)({
  fontSize: '24px',
  fontWeight: 500,
});

const StatLabel = styled(Typography)({
  fontSize: '14px',
  color: '#666',
});

function getDateRangeGranularity(
  dateRange: [Date | null, Date | null]
): DateGroupType {
  if (!dateRange[0] || !dateRange[1]) return DateGroupType.Day;
  const daysBetween = Math.ceil(
    (dateRange[1].getTime() - dateRange[0].getTime()) / (1000 * 60 * 60 * 24)
  );
  if (daysBetween <= 60) return DateGroupType.Day;
  if (daysBetween <= 420) return DateGroupType.Week;
  if (daysBetween <= 1800) return DateGroupType.Month;
  return DateGroupType.Year;
}

const InsoleUsageDetailContent: React.FC<Props> = ({
  patientId,
  selectedDateRange: externalDateRange,
}) => {
  const dispatch = useAppThunkDispatch();
  const { t } = useTranslation('activity');

  const insoleData = useSelector(processedInsoleUsageData);
  const selectedDailyMetricDate = useSelector(selectedMetricDate);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [processedData, setProcessedData] = useState<
    IProcessedActivityTrackerInterval[]
  >([]);

  // Ref for the combined chart
  const combinedChartRef = useRef<any>(null);

  // Internal date range state - initialize based on selected date from Redux or today's date
  const [internalDateRange, setInternalDateRange] = useState<[Date | null, Date | null]>(() => {
    if (selectedDailyMetricDate) {
      const selectedDate = new Date(selectedDailyMetricDate);
      return [selectedDate, selectedDate];
    }
    const today = new Date();
    return [today, today];
  });

  // Sync internal date range when the Redux selected date changes (e.g. header date picker)
  useEffect(() => {
    if (selectedDailyMetricDate) {
      const selectedDate = new Date(selectedDailyMetricDate);
      setInternalDateRange([selectedDate, selectedDate]);
    }
  }, [selectedDailyMetricDate]);

  // Use external date range if provided, otherwise use internal
  const selectedDateRange = externalDateRange || internalDateRange;

  // Reset zoom on chart
  const handleResetZoom = useCallback(() => {
    if (combinedChartRef.current) {
      combinedChartRef.current.resetZoom();
    }
  }, []);

  // Stats
  const [totalWalk, setTotalWalk] = useState<number>(0);
  const [totalSit, setTotalSit] = useState<number>(0);
  const [totalStand, setTotalStand] = useState<number>(0);
  const [totalOther, setTotalOther] = useState<number>(0);

  useEffect(() => {
    if (patientId && selectedDateRange[0] && selectedDateRange[1]) {
      setIsLoading(true);
      dispatch(
        getPassiveParameterData(
          patientId,
          selectedDateRange[0],
          selectedDateRange[1],
          getDateRangeGranularity(selectedDateRange),
          PassiveParameterTypeEnum.InsoleUsage
        )
      ).finally(() => {
        setIsLoading(false);
      });
    }
  }, [dispatch, patientId, selectedDateRange]);

  useEffect(() => {
    // Process data and calculate statistics
    const sortedData = [...insoleData].sort((a, b) =>
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );
    setProcessedData(sortedData);

    if (insoleData.length > 0) {
      setTotalWalk(insoleData.reduce((sum, item) => sum + item.walk, 0));
      setTotalSit(insoleData.reduce((sum, item) => sum + item.sit, 0));
      setTotalStand(insoleData.reduce((sum, item) => sum + item.stand, 0));
      setTotalOther(insoleData.reduce((sum, item) => sum + item.other, 0));
    } else {
      setTotalWalk(0);
      setTotalSit(0);
      setTotalStand(0);
      setTotalOther(0);
    }
  }, [insoleData, selectedDateRange]);

  const formatMinutes = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  return (
    <Root>
      {/* Date Picker and Reset Zoom Button */}
      <DatePickerContainer>
        <ResetZoomButton
          variant="outlined"
          startIcon={<RestartAltIcon />}
          onClick={handleResetZoom}
        >
          {t('resetZoom')}
        </ResetZoomButton>
        <MultiDatePicker
          isRangePicker={true}
          selectedRange={internalDateRange}
          setSelectedRange={setInternalDateRange}
        />
      </DatePickerContainer>

      {isLoading && (
        <LoadingContainer>
          <CircularProgress />
        </LoadingContainer>
      )}

      {processedData.length === 0 && !isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Typography variant="body1" color="textSecondary">
            {t('noRangeData')}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {/* Combined Activity Chart */}
          <Grid size={12}>
            <ChartPaper elevation={2}>
              <Typography variant="h6" gutterBottom>
                {t('sessionsOverTime')}
              </Typography>
              <ChartContainer>
                <CombinedActivityChart
                  data={processedData}
                  selectedDateRange={selectedDateRange}
                  dateType={getDateRangeGranularity(selectedDateRange)}
                  chartRef={combinedChartRef}

                />
              </ChartContainer>
            </ChartPaper>
          </Grid>
        </Grid>
      )}

      <StatsContainer container spacing={3}>
        <Grid size={{ xs: 12, md: 3 }}>
          <StatsCard>
            <StatValue>{formatMinutes(totalWalk)}</StatValue>
            <StatLabel>{t('totals.walk')}</StatLabel>
          </StatsCard>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <StatsCard>
            <StatValue>{formatMinutes(totalSit)}</StatValue>
            <StatLabel>{t('totals.sit')}</StatLabel>
          </StatsCard>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <StatsCard>
            <StatValue>{formatMinutes(totalStand)}</StatValue>
            <StatLabel>{t('totals.stand')}</StatLabel>
          </StatsCard>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <StatsCard>
            <StatValue>{formatMinutes(totalOther)}</StatValue>
            <StatLabel>{t('totals.other')}</StatLabel>
          </StatsCard>
        </Grid>
      </StatsContainer>
    </Root>
  );
};

export default InsoleUsageDetailContent;

